import React, { useState, useEffect } from 'react';
import { 
  Star, 
  Send, 
  X, 
  User, 
  MessageSquare, 
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import Button from '../ui/Button';
import Card from '../ui/Card';
import toast from 'react-hot-toast';
import { dataHelpers } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const ReviewForm = ({ 
  onSubmit, 
  onCancel, 
  initialData = null, 
  loading = false,
  studentId = null,
  parentId = null,
  parentName = null 
}) => {
  const { company } = useAuth();
  
  const [formData, setFormData] = useState({
    rating: initialData?.rating || 0,
    comment: initialData?.comment || '',
    student_id: initialData?.student_id || studentId || '',
    parent_id: initialData?.parent_id || parentId || '',
    parent_name: initialData?.parent_name || parentName || ''
  });

  const [errors, setErrors] = useState({});
  const [hoveredRating, setHoveredRating] = useState(0);
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(true);

  useEffect(() => {
    if (company?.id && !studentId) {
      loadStudents();
    } else {
      setLoadingStudents(false);
    }
  }, [company, studentId]);

  const loadStudents = async () => {
    try {
      setLoadingStudents(true);
      const studentsData = await dataHelpers.getStudents(company.id);
      setStudents(studentsData || []);
    } catch (error) {
      console.error('Error loading students:', error);
      toast.error('Erro ao carregar alunos');
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleRatingClick = (rating) => {
    setFormData(prev => ({ ...prev, rating }));
    if (errors.rating) {
      setErrors(prev => ({ ...prev, rating: '' }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Limpar erro do campo
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.rating || formData.rating < 1 || formData.rating > 5) {
      newErrors.rating = 'Por favor, selecione uma avaliação de 1 a 5 estrelas';
    }

    if (!formData.comment.trim()) {
      newErrors.comment = 'Por favor, escreva um comentário sobre sua experiência';
    } else if (formData.comment.trim().length < 10) {
      newErrors.comment = 'O comentário deve ter pelo menos 10 caracteres';
    } else if (formData.comment.trim().length > 500) {
      newErrors.comment = 'O comentário deve ter no máximo 500 caracteres';
    }

    if (!studentId && !formData.student_id) {
      newErrors.student_id = 'Por favor, selecione um aluno';
    }

    if (!parentId && !formData.parent_name.trim()) {
      newErrors.parent_name = 'Nome do responsável é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Por favor, corrija os erros no formulário');
      return;
    }

    try {
      const reviewData = {
        rating: formData.rating,
        comment: formData.comment.trim(),
        student_id: formData.student_id,
        parent_id: formData.parent_id,
        parent_name: formData.parent_name.trim()
      };

      await onSubmit(reviewData);
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error('Erro ao enviar avaliação');
    }
  };

  const renderStars = () => {
    return Array.from({ length: 5 }, (_, index) => {
      const starValue = index + 1;
      const isActive = starValue <= (hoveredRating || formData.rating);
      
      return (
        <button
          key={index}
          type="button"
          onMouseEnter={() => setHoveredRating(starValue)}
          onMouseLeave={() => setHoveredRating(0)}
          onClick={() => handleRatingClick(starValue)}
          className={`p-1 transition-colors duration-200 ${
            isActive 
              ? 'text-yellow-400 hover:text-yellow-500' 
              : 'text-gray-300 hover:text-yellow-300'
          }`}
        >
          <Star 
            className={`w-8 h-8 ${isActive ? 'fill-current' : ''}`} 
          />
        </button>
      );
    });
  };

  const getRatingLabel = (rating) => {
    const labels = {
      1: 'Muito Insatisfeito',
      2: 'Insatisfeito',
      3: 'Regular',
      4: 'Satisfeito',
      5: 'Muito Satisfeito'
    };
    return labels[rating] || '';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Star className="w-6 h-6 text-yellow-500 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">
              {initialData ? 'Editar Avaliação' : 'Nova Avaliação'}
            </h2>
          </div>
          <button
            onClick={onCancel}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Seleção de Aluno - Apenas se não foi pré-definido */}
          {!studentId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Aluno *
              </label>
              <select
                name="student_id"
                value={formData.student_id}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.student_id ? 'border-red-300' : 'border-gray-300'
                }`}
                required
                disabled={loadingStudents}
              >
                <option value="">
                  {loadingStudents ? 'Carregando...' : 'Selecione o aluno'}
                </option>
                {students.map(student => (
                  <option key={student.id} value={student.id}>
                    {student.name} - {student.registration_number}
                  </option>
                ))}
              </select>
              {errors.student_id && (
                <p className="mt-1 text-sm text-red-600">{errors.student_id}</p>
              )}
            </div>
          )}

          {/* Nome do Responsável - Apenas se não foi pré-definido */}
          {!parentId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Seu Nome *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  name="parent_name"
                  value={formData.parent_name}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.parent_name ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Digite seu nome completo"
                  required
                />
              </div>
              {errors.parent_name && (
                <p className="mt-1 text-sm text-red-600">{errors.parent_name}</p>
              )}
            </div>
          )}

          {/* Avaliação com Estrelas */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Avaliação Geral *
            </label>
            <Card className="p-4">
              <div className="text-center">
                <div className="flex justify-center space-x-1 mb-3">
                  {renderStars()}
                </div>
                
                {(hoveredRating || formData.rating) > 0 && (
                  <p className="text-sm font-medium text-gray-700">
                    {getRatingLabel(hoveredRating || formData.rating)}
                  </p>
                )}
                
                {!formData.rating && (
                  <p className="text-sm text-gray-500">
                    Clique nas estrelas para avaliar
                  </p>
                )}
              </div>
            </Card>
            {errors.rating && (
              <p className="mt-1 text-sm text-red-600">{errors.rating}</p>
            )}
          </div>

          {/* Comentário */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Comentário *
            </label>
            <div className="relative">
              <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <textarea
                name="comment"
                value={formData.comment}
                onChange={handleChange}
                rows={4}
                className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${
                  errors.comment ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Conte-nos sobre sua experiência com nossos serviços..."
                required
                maxLength={500}
              />
            </div>
            <div className="flex justify-between items-center mt-1">
              {errors.comment ? (
                <p className="text-sm text-red-600">{errors.comment}</p>
              ) : (
                <p className="text-sm text-gray-500">
                  Conte-nos sobre sua experiência
                </p>
              )}
              <p className="text-xs text-gray-400">
                {formData.comment.length}/500
              </p>
            </div>
          </div>

          {/* Dica de Feedback */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex">
              <CheckCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Dicas para uma boa avaliação:</p>
                <ul className="list-disc list-inside space-y-1 text-blue-700">
                  <li>Seja específico sobre sua experiência</li>
                  <li>Mencione pontos positivos e sugestões de melhoria</li>
                  <li>Seja respeitoso e construtivo</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Botões */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Button
              type="submit"
              variant="primary"
              icon={Send}
              loading={loading}
              className="flex-1"
            >
              {initialData ? 'Atualizar Avaliação' : 'Enviar Avaliação'}
            </Button>
            
            <Button
              type="button"
              variant="outline"
              icon={X}
              onClick={onCancel}
              className="flex-1"
            >
              Cancelar
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReviewForm;